import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useContent } from "@/lib/useContent";
import { createBooking, getTakenSlots, getBlockedDates } from "@/lib/api";

export default function BookingSection() {
  const { content } = useContent();
  const timeSlots = content.timeSlots || [];

  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [taken, setTaken] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location_preference: "",
    message: "",
  });

  const refresh = () => {
    getTakenSlots().then(setTaken).catch(() => {});
    getBlockedDates().then(setBlocked).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const dateKey = date ? format(date, "yyyy-MM-dd") : null;
  const takenForDate = dateKey ? taken[dateKey] || [] : [];
  const isBlockedDay = dateKey && blocked.includes(dateKey);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Choose a date and a time.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("A name and an address, please.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createBooking({
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time_slot: slot,
      });
      setConfirmed(res);
      toast.success("Reserved. He'll be in touch.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went sideways.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative border-t border-[#2C2C2C] bg-[#080808]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-12 gap-6 md:gap-14">
          <div className="col-span-12 md:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#C67022] mb-6">
              05 / Reservation
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-black text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]"
            >
              Book <em className="italic text-[#C67022]">the night.</em>
            </motion.h2>
            <p className="mt-8 text-white/60 font-light leading-relaxed max-w-md">
              He answers by hand. Choose an evening, tell him where to find you, and expect a reply before midnight &mdash; his, not yours.
            </p>

            <div className="mt-14 space-y-3 font-mono text-[11px] tracking-[0.25em] uppercase text-white/40">
              <div className="flex justify-between"><span>Response</span><span className="text-white/70">≤ 24h</span></div>
              <div className="divider-h" />
              <div className="flex justify-between"><span>Discretion</span><span className="text-white/70">Absolute</span></div>
              <div className="divider-h" />
              <div className="flex justify-between"><span>Cancellation</span><span className="text-white/70">48h Notice</span></div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {confirmed ? (
              <motion.div
                data-testid="booking-confirmation"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[#C67022] bg-[#121212] p-10 md:p-14"
              >
                <div className="font-mono text-[10px] tracking-[0.35em] text-[#C67022] mb-6">CONFIRMED</div>
                <div className="font-display text-4xl md:text-5xl leading-tight tracking-tighter mb-6">
                  Kept for you, <em className="italic">{confirmed.name.split(" ")[0]}</em>.
                </div>
                <div className="grid grid-cols-2 gap-4 font-mono text-[11px] tracking-[0.2em] uppercase text-white/60 mb-8">
                  <div><div className="text-white/40 mb-1">Date</div><div className="text-white text-lg font-display normal-case tracking-normal">{confirmed.date}</div></div>
                  <div><div className="text-white/40 mb-1">Time</div><div className="text-white text-lg font-display normal-case tracking-normal">{confirmed.time_slot}</div></div>
                </div>
                <div className="text-white/60 leading-relaxed">
                  A confirmation has been sent to Trevor. He&apos;ll reach out at <span className="text-[#C67022]">{confirmed.email}</span> personally.
                </div>
                <button
                  data-testid="booking-new"
                  onClick={() => { setConfirmed(null); setDate(null); setSlot(null); setForm({ name:"", email:"", phone:"", location_preference:"", message:"" }); }}
                  className="mt-10 font-mono text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-[#C67022] transition-colors"
                >
                  ← Reserve another night
                </button>
              </motion.div>
            ) : (
              <motion.form
                onSubmit={submit}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="border border-[#2C2C2C] bg-[#0d0d0d]"
              >
                <div className="grid md:grid-cols-2">
                  <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-[#2C2C2C]">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Choose a date</div>
                    <Calendar
                      data-testid="booking-calendar"
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setSlot(null); }}
                      disabled={(d) => {
                        if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                        const key = format(d, "yyyy-MM-dd");
                        return blocked.includes(key);
                      }}
                      className="text-white"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">Choose a time</div>
                    <div className="grid grid-cols-2 gap-2">
                      {timeSlots.map((t) => {
                        const isTaken = takenForDate.includes(t);
                        const isSelected = slot === t;
                        return (
                          <button
                            key={t}
                            type="button"
                            data-testid={`slot-${t}`}
                            disabled={isTaken || !date || isBlockedDay}
                            onClick={() => setSlot(t)}
                            className={`py-3 border font-mono text-sm tracking-[0.2em] transition-colors ${
                              isSelected
                                ? "border-[#C67022] bg-[#C67022] text-[#080808]"
                                : isTaken
                                ? "border-[#2C2C2C] text-white/20 line-through cursor-not-allowed"
                                : !date || isBlockedDay
                                ? "border-[#2C2C2C] text-white/20 cursor-not-allowed"
                                : "border-[#2C2C2C] text-white/70 hover:border-[#C67022] hover:text-[#C67022]"
                            }`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                      {date ? format(date, "EEEE · MMM d, yyyy") : "Select a date to reveal slots"}
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#2C2C2C] p-6 md:p-8 grid md:grid-cols-2 gap-4">
                  <FormField label="Name">
                    <Input
                      data-testid="input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] h-11 text-lg font-display"
                      placeholder="Your full name"
                    />
                  </FormField>
                  <FormField label="Email">
                    <Input
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] h-11 text-lg font-display"
                      placeholder="you@somewhere.com"
                    />
                  </FormField>
                  <FormField label="Phone (optional)">
                    <Input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] h-11 text-lg font-display"
                      placeholder="+1 —"
                    />
                  </FormField>
                  <FormField label="Where">
                    <Input
                      data-testid="input-location"
                      value={form.location_preference}
                      onChange={(e) => setForm({ ...form, location_preference: e.target.value })}
                      className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] h-11 text-lg font-display"
                      placeholder="Silver Lake · Downtown · Yours"
                    />
                  </FormField>
                  <div className="md:col-span-2">
                    <FormField label="A note">
                      <Textarea
                        data-testid="input-message"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={3}
                        className="bg-transparent border-0 border-b border-[#2C2C2C] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#C67022] text-lg font-display resize-none"
                        placeholder="Tell him something worth reading before midnight."
                      />
                    </FormField>
                  </div>
                </div>

                <div className="border-t border-[#2C2C2C] p-6 md:p-8 flex items-center justify-between gap-6">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                    All fields discreet · No newsletters.
                  </div>
                  <button
                    type="submit"
                    data-testid="submit-booking"
                    disabled={submitting}
                    className="group inline-flex items-center gap-4 border border-[#C67022] bg-[#C67022] text-[#080808] hover:bg-[#E68A35] hover:border-[#E68A35] disabled:opacity-50 font-mono text-[11px] tracking-[0.3em] uppercase px-8 py-4 transition-colors"
                  >
                    {submitting ? "Sending…" : "Send Request"}
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </motion.form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2">{label}</span>
      {children}
    </label>
  );
}
